/**
 * The public area's route components, loaded lazily and built as one chunk
 * under `/areas/public/`. These pages use no React Aria components (sign-in
 * uses native inputs), so they skip `withAria` and keep the public bundle
 * small; wrap a page with it as soon as it does.
 */
import { createLazyRoute, useParams } from '@tanstack/react-router';

import { WeChatDonePage } from '../../features/channel-connections/WeChatDonePage.js';
import { LinkPage } from '../../features/secure-link/LinkPage.js';
import { SignInPage } from '../../features/sign-in/SignInPage.js';
import { NotMePage } from '../../features/sign-in/NotMePage.js';
import { VerifyPage } from '../../features/sign-in/VerifyPage.js';
import { WelcomePage } from '../../features/sign-in/WelcomePage.js';

/** The lazy half of the sign-in route. */
export const signInRoute = createLazyRoute('/sign-in')({ component: SignInPage });

/** The lazy half of the magic-link landing route. */
export const verifyRoute = createLazyRoute('/auth/verify')({ component: VerifyPage });

/** The lazy half of the "your account is being set up" route. */
export const welcomeRoute = createLazyRoute('/welcome')({ component: WelcomePage });

/** The lazy half of the "This wasn’t me" route (new-device emails). */
export const notMeRoute = createLazyRoute('/auth/not-me')({ component: NotMePage });

/** A secure link's landing page, keyed by its ID (a new link is a fresh page). */
function SecureLinkPage() {
  const params: Record<string, string | undefined> = useParams({ strict: false });
  const linkId = params.linkId ?? '';
  return <LinkPage key={linkId} linkId={linkId} />;
}

/** The lazy half of the secure-link landing route (`/l/:linkId#secret`, H3.1). */
export const linkRoute = createLazyRoute('/l/$linkId')({ component: SecureLinkPage });

/** The lazy half of the page WeChat's browser shows after "Connect WeChat" (H6.2). */
export const weChatDoneRoute = createLazyRoute('/connected/wechat')({ component: WeChatDonePage });
